import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwiseLight, PiCheckCircleLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import { getBrandingAffected, reapplyBranding, BrandingReapplyResult } from '../../utils/brandingReapplyApi';

interface Props {
  /** The saved branding template's UUID. Nothing renders for an unsaved template. */
  templateId?: string | null;
  /** Bump this (e.g. after a save) to re-check the affected count. */
  refreshKey?: number | string;
}

/**
 * Branded Templates (Wave 5) - "N documents use an older version of this branding".
 *
 * Passively checks how many finalized drafts were branded with an older version of
 * this template and offers a one-click re-apply (a safe, deterministic restyle that
 * saves a new, restorable version of each). Renders nothing when there's an unsaved
 * template or nothing is behind.
 */
const BrandingReapplyBanner: React.FC<Props> = ({ templateId, refreshKey }) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BrandingReapplyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    if (!templateId) { setCount(0); return; }
    try {
      const res = await getBrandingAffected(templateId);
      setCount(res.count);
    } catch {
      setCount(0); // never block the editor on this passive check
    }
  }, [templateId]);

  useEffect(() => { check(); }, [check, refreshKey]);

  const onReapply = useCallback(async () => {
    if (!templateId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reapplyBranding(templateId);
      setResult(res);
      await check();
    } catch (e: any) {
      setError(t('Could not re-apply branding. Please try again.'));
    } finally {
      setBusy(false);
    }
  }, [templateId, check, t]);

  if (!templateId) return null;

  if (result && result.applied > 0 && count === 0) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-900 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300">
        <PiCheckCircleLight className="h-4 w-4 flex-shrink-0" />
        <span>
          {t('Re-applied the updated branding to {{n}} document(s).', { n: result.applied })}
          {result.failed > 0 ? ' ' + t('{{f}} could not be updated.', { f: result.failed }) : ''}
        </span>
      </div>
    );
  }

  if (count === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-info-200 bg-info-50 px-4 py-3 text-info-900 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm">
          <span className="font-medium">
            {t('{{n}} finalized document(s) use an older version of this branding.', { n: count })}
          </span>
          <span className="block text-info-800/80 dark:text-info-300/80 text-xs mt-0.5">
            {t('Re-apply the current branding to bring them up to date. This restyles them and saves a new, restorable version — it never changes the wording.')}
          </span>
        </div>
        <Button
          variant="primary"
          size="small"
          type="button"
          onClick={onReapply}
          disabled={busy}
          isLoading={busy}
          icon={busy ? undefined : <PiArrowsClockwiseLight className="h-4 w-4" />}
        >
          {busy ? t('Re-applying…') : t('Re-apply to {{n}} document(s)', { n: count })}
        </Button>
      </div>
      {error && <div className="mt-2 text-xs text-danger-600">{error}</div>}
    </div>
  );
};

export default BrandingReapplyBanner;
